import { useEffect, useState } from "react";
import {
  AnalyticsDataResponse,
  AnalyticsDetailsResponse
} from "@queries/Analytics";
import { ChartSkeleton } from "src/pages/analytics/skeleton/ChartSkeleton";
import useAnalyticsData from "@hooks/analytics/useAnalyticsData";
import {
  ANALYTICS_CHART_TYPE,
  DEBOUNCE_TIME_MILLISECOND
} from "@resources/Constants";
import { debounce } from "lodash";
import { useAppDispatch, useAppSelector } from "@configs/Redux";
import {
  setActiveReportAnalyticsQueueData,
  setBackupAnalyticsQueue
} from "@redux/Report";
import { TypeTimeInterval } from "@custom-types/Types";
import { PeopleQueueChartPresenter } from "./Presenter";
import { ChartCardProps } from "../Interfaces";
import { defaultDataVehicleType } from "../PreviewData";

const PeopleQueueChartContainer = (props: ChartCardProps) => {
  const {
    type,
    baseTime: { ...baseTime },
    options,
    isSingleLocation,
    analyticsFromReport,
    rules,
    order,
    title
  } = props;
  const dispatch = useAppDispatch();
  const { analyticsQueueData, callAnalyticsQueueApi } = useAppSelector(
    (state) => state.report
  );

  const [selected, setSelected] = useState<string[]>(defaultDataVehicleType);
  const [viewType, setViewType] = useState<string>(options?.viewList[0] ?? "");
  const [isLoadingChart, setIsLoadingChart] = useState<boolean>(true);
  const [savedAnalytics, setSavedAnalytics] = useState<
    (AnalyticsDetailsResponse & { key: string })[]
  >([]);
  const isAnalyticsType: Set<string> = new Set();

  const {
    analyticsDatas,
    dataSeries,
    isLoading,
    updatedAnalytics,
    timeInterval,
    typeInterval,
    setType
  } = useAnalyticsData(
    type || ANALYTICS_CHART_TYPE.PEOPLE_QUEUES,
    baseTime,
    viewType,
    rules ?? {},
    true
  );

  const handleAnalyticsFromReport = () => {
    const savedAnalytics = analyticsQueueData?.find(
      (item) => item.order === order
    );
    if (savedAnalytics && !callAnalyticsQueueApi) {
      setSavedAnalytics(
        savedAnalytics.data as (AnalyticsDetailsResponse & { key: string })[]
      );
      setIsLoadingChart(false);
      setViewType(options?.viewList[0] ?? "");
      return;
    }

    setIsLoadingChart(true);
    setViewType(options?.viewList[0] ?? "");
    setSelected(defaultDataVehicleType);
    updatedAnalytics(
      [],
      props.typeInterval as TypeTimeInterval,
      options?.viewList[0] ?? "",
      isSingleLocation
    );
    isAnalyticsType.add(title);
  };

  useEffect(() => {
    if (analyticsFromReport && analyticsDatas.length && order) {
      dispatch(
        setActiveReportAnalyticsQueueData({
          data: analyticsDatas,
          order
        })
      );
      dispatch(
        setBackupAnalyticsQueue({
          data: analyticsDatas,
          order
        })
      );
    }
  }, [JSON.stringify(analyticsDatas)]);

  useEffect(() => {
    const debouncedRefetch = debounce(() => {
      if ((!baseTime?.start && !baseTime?.end) || !baseTime) return;
      if (!rules) return;
      if (analyticsFromReport) {
        handleAnalyticsFromReport();
        return;
      }
      if (!isAnalyticsType.has(title)) {
        setIsLoadingChart(true);
        setViewType(options?.viewList[0] ?? "");
        setSelected(defaultDataVehicleType);
        updatedAnalytics(
          [],
          null,
          options?.viewList[0] ?? "",
          isSingleLocation
        );
        isAnalyticsType.add(title);
      }
    }, DEBOUNCE_TIME_MILLISECOND);
    debouncedRefetch();
    return () => debouncedRefetch.cancel();
  }, [JSON.stringify(baseTime), rules]);

  if (isLoading && !savedAnalytics && isLoadingChart) {
    return <ChartSkeleton />;
  }

  return (
    <div>
      <PeopleQueueChartPresenter
        {...props}
        dataSeries={dataSeries}
        analyticsData={
          (savedAnalytics as AnalyticsDataResponse[]).length
            ? (savedAnalytics as AnalyticsDataResponse[])
            : (analyticsDatas as AnalyticsDataResponse[])
        }
        updatedAnalytics={updatedAnalytics}
        selected={selected}
        setSelected={setSelected}
        setViewType={setViewType}
        viewType={viewType}
        timeInterval={
          props.analyticsFromReport ? props.timeInterval : timeInterval
        }
        typeInterval={
          props.analyticsFromReport ? props.typeInterval : typeInterval
        }
        setType={setType}
        widgetType={type}
        isLoading={isLoading && !savedAnalytics}
        setIsLoadingChart={setIsLoadingChart}
      />
    </div>
  );
};

export default PeopleQueueChartContainer;
